import axios from 'axios';
import { API_BASE_URL } from '@/constants/domain';
import { useAuth } from '@/zustand/auth-store';
import { showError } from '@/libs/react.toastify';
import i18n from '@/i18n';
import { AuthApis } from '@/apis/auth/api';
import { getLoginPath } from '@/utils/helpers';
import { USER_TYPES } from '@/constants/constants';
import { isAccountDeactivatedError } from '@/utils/account-selection';

const ApiInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Refresh Token Queue + State
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

const extractTokens = response => response?.token || response || {};

// Helpers
const attachLanguageHeader = config => {
  config.headers['Accept-Language'] = i18n.language || 'en';
};

const attachAccessToken = config => {
  // The request carries its own Authorization (e.g. select-account's selection token).
  if (config?.skipAuthHeader) {
    return;
  }
  const token = useAuth.getState().accessToken;
  const preAuthToken = useAuth.getState()?.preAuthToken;
  if (config?.useAlternateToken) {
    if (preAuthToken) {
      config.headers.Authorization = `Bearer ${preAuthToken}`;
    }
    return;
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
};

// REQUEST INTERCEPTOR
ApiInstance.interceptors.request.use(
  config => {
    attachLanguageHeader(config);
    attachAccessToken(config);

    // CRITICAL: For FormData, we must delete Content-Type header
    // so the browser can set it automatically with the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// RESPONSE INTERCEPTOR (401 Refresh Logic)
export const setupInterceptors = (navigate, t) => {
  void t;
  ApiInstance.interceptors.response.use(
    res => res,
    async error => {
      const originalReq = error.config;

      // 500 SERVER ERROR
      if (error.response?.status === 500) {
        showError(i18n.t('errors.500', { ns: 'common' }));
        return Promise.reject(error);
      }

      const status = error.response?.status;

      // ACCOUNT_DEACTIVATED: this account was deactivated or discharged (401 on any call,
      // 403 on refresh). The person's other accounts still work after a new sign in.
      if (isAccountDeactivatedError(error)) {
        if (!originalReq?.skipRedirect) {
          useAuth.getState().clearAuth();
          showError(error.response.data.message);
          navigate(getLoginPath(), { replace: true });
        }
        return Promise.reject(error);
      }

      // 403 FORBIDDEN
      if (status === 403 && !originalReq?.skipRedirect) {
        navigate('/permission-denied', { replace: true });
        return Promise.reject(error);
      }

      // 404 NOT FOUND
      if (status === 404 && !originalReq?.skipRedirect) {
        navigate('/404', { replace: true });
        return Promise.reject(error);
      }

      // 401 UNAUTHORIZED → TRY REFRESH (by user type)
      if (error.response?.status === 401 && !originalReq._retry) {
        if (originalReq?.skipRefresh) {
          if (!originalReq?.skipRedirect) {
            useAuth.getState().clearAuth();
            navigate(getLoginPath(), { replace: true });
          }
          return Promise.reject(error);
        }

        // No refresh token means the user already signed out (logout cleared
        // it) - a query still in flight or refetching on that last render
        // 401s, and there's nothing to refresh. Treat it as signed-out, not as
        // an expired session, so logout doesn't land on /link-expired.
        if (!useAuth.getState().refreshToken) {
          navigate(getLoginPath(), { replace: true });
          return Promise.reject(error);
        }

        originalReq._retry = true;
        const userType = localStorage.getItem('user-type');

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(newToken => {
              originalReq.headers.Authorization = `Bearer ${newToken}`;
              return ApiInstance(originalReq);
            })
            .catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        const refreshFn =
          userType === USER_TYPES.DASHBOARD
            ? AuthApis.dashboardRefreshAccessToken
            : AuthApis.refreshAccessToken;

        try {
          const response = await refreshFn();
          const { accessToken, refreshToken } = extractTokens(response);

          if (!accessToken || !refreshToken) {
            throw new Error('Refresh token response did not include new tokens');
          }

          useAuth.getState().setTokens({ accessToken, refreshToken });
          ApiInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

          processQueue(null, accessToken);
          isRefreshing = false;

          originalReq.headers.Authorization = `Bearer ${accessToken}`;
          return ApiInstance(originalReq);
        } catch (refreshErr) {
          processQueue(refreshErr, null);
          isRefreshing = false;

          // A deactivated account was already sent to sign in above.
          if (!isAccountDeactivatedError(refreshErr)) {
            useAuth.getState().clearAuth();
            navigate('/link-expired', { replace: true });
          }

          return Promise.reject(refreshErr);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default ApiInstance;
